/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import isObject from 'lodash/isObject';
import isUndefined from 'lodash/isUndefined';

// TODO IoC/DI - Musel by se asi refaktorizovat celý schedule modul.
import IcalPlanner from '../ical/ical-planner';

// TODO #458 Refaktorovat na menší třídy
// - Funkcionalita související s rozměry a pozicováním (width, top, zIndex, ...).
//   - Lepší zpracování metod pro zjišťování rozměrů (DRY, defaults)?
// - Funkcionalita související s pořadím (cycleIndex a iterationIndex).
//   - Pomohlo by hackům při plánování ScheduleLinear
// TODO #138 Přepracovat přístup k prázdnému `ScheduleInfo`.
// - Předchozí stav:
//   - Prázdný slajd je reprezentován jako `null`.
//   - Obecně nyní všechny strategie vracejí buď samé slajdy, nebo samé `null`.
//   - Při plánování jsou prázdné slajdy přeskakovány.
// - Aktuální stav:
//   - Byly refaktorovány všechny `null` na `new ScheduleInfo()`.
//   - K detekci se používá `ScheduleInfo#isEmpty`
// - Objevený problém:
//   - Nastavá v okamžiku slučování obsahů z vnitřních strategií, kdy je třeba seřadit jejich výsledky.
//   - Jelikož u prázdného slajdu nelze určit `cycleIndex` a `iterationIndex`, je nutné prázdné slajdy ze slučování vyjmout.
//   - To pak ale způsobuje dopředu nepředvídatelné zkracování plánů z vnitřních strategií.
//   - Teoreticky může nějaká strategie vracet zároveň prázdné i neprázdné slajdy.
//   - Pak nelze dopředu odhadnout kolik jejích slajdů se má plánovat (hrozba zacyklení).
// - Aktuální řešení:
//   - Spoléhá se na to, že výsledkem jsou buď samé neprázdné slajdy, nebo samé prázdné slajdy.
//   - Všechny prázdné slajdy se od začátku odeberou a nahradí za ostatní slajdy.
//   - Pokud zde nejsou žádné neprázdné slajdy, tak se vracejí samé prázdné slajdy.
// - Lepší ošetření:
//   - Jinak zpracovat systém slučování plánů?
// TODO #295 Vylepšit implementaci. Není třeba hledat v registru opakovaně.
// - `isPreloadable`, `isViewable` + ještě i přístup k `getViewerType` působí příliš duplicitně.
// - Idálně by se asi měl v konstruktoru nahradit `ViewerRegistry` za již vyhledaný `ViewerType`.
//   Ten pak zpřistupnit pomocí getViewerType a dále v této třídě neřešit.
// - Vůbec nedovolit vytvoření `ScheduleInfo` bez `ViewerType`, aby nebylo nutné řešit jeho chybění?
/**
 * Informace potřebné k přehrání média.
 * Určené pro použití mimo plánovač.
 * Obsahuje hodnoty vypočtené v určitý okamžik.
 * Slouží pro odstínění logiky určení hodnot dle nějaké strategie.
 * Každá strategie určuje hodnoty jinak, ale vrací je v tomto jednotném formátu.
 * Obsahuje všechny informace, které může potřebovat prolínač, rodičovská strategie, analytika, ...
 * @implements {Serializable}
 * @implements {Stringable}
 */
class ScheduleInfo {
  /**
   * Vytvoří instanci.
   * @param {Object} [opts={}] Nepovinné parametry.
   * @param {number} [opts.cycleIndex] Pořadí cyklu do kterého slajd patří.
   * @param {number} [opts.iterationIndex] Pořadí slajdu v rámci aktuálně přehrávaného cyklu.
   * @param {Layer} [opts.layer] Vrstva, do které se přehrávané médium vykresluje.
   * @param {Layout} [opts.layout] Šablona, do které se přehrávané médium vykresluje.
   * @param {Medium} [opts.medium] Médium k přehrání.
   * @param {Array.<Playlist>} [opts.playlistArray] Seznam playlistů, do kterých médium patří.
   * @param {Session} [opts.session] Nastavení zařízení, na kterém se médium přehrává.
   * @param {Array.<Track>} [opts.trackArray] Seznam stop playlistů, do kterých médium patří.
   * @param {ViewerRegistry} [opts.viewerRegistry] Registr informací o typech médií.
   * @returns {ScheduleInfo} Nová instance.
   */
  constructor({
    cycleIndex,
    iterationIndex,
    layer,
    layout,
    medium,
    playlistArray = [],
    session,
    trackArray = [],
    viewerRegistry
  } = {}) {
    this.cycleIndex = cycleIndex;
    this.iterationIndex = iterationIndex;
    this.layer = layer;
    this.layout = layout;
    this.medium = medium;
    this.playlistArray = playlistArray;
    this.session = session;
    this.trackArray = trackArray;
    this.viewerRegistry = viewerRegistry;
  }

  /**
   * Zjistí barvu pozadí slajdu.
   * Hodnota odpovídá [CSS vlastnosti background-color](https://developer.mozilla.org/en-US/docs/Web/CSS/background-color).
   * @returns {string} Barva.
   */
  getBackgroundColor() {
    // TODO #270 Prototyp nastavení pozadí, jak zpracovat lépe?
    // - Předávat do ScheduleInfo layout? Jak pak identifikovat vrstvu s pozadím?
    //   - Aktuální zpracování s tím, že se dočasně pozadí identifikuje jako ScheduleInfo se šablonou, ale bez vrstvy.
    // - Vytvořit novou implementaci ScheduleInfo?
    //   - To by vyžadovalo propojení jednotlivých ScheduleInfo do stromu.
    //     Delegování požadavků na podřízené ScheduleInfo a rekace jde jejich výsledků?
    // - Předávat do ScheduleInfo backgroundColor?
    //   - To nebude fungovat, jakmile budou šablony zanořené.
    //     Hodnotu přepíše rodičovská Schedule info pro vrstvu.
    //   - Toto asi nebude vhodné, jelikož to odpovídá starému opuštěnému stylu.
    const { layer, layout } = this;
    if (!isNil(layer)) {
      // Výchozí barva pro pozadí vrstev.
      return '#000000';
    }
    if (!isNil(layout)) {
      // Barva pro pozadí šablony.
      const { backgroundColor } = layout;
      if (isNil(backgroundColor)) {
        throw new TypeError('Missing background color.');
      }
      return backgroundColor;
    }
    // Výchozí barva pro slajdy bez šablon.
    return '#000000';
  }

  /**
   * Zjistí pořadí cyklu do kterého slajd patří.
   * Cyklus je série slajdů po kterých se začnou média opakovat.
   * @returns {number} Pořadí.
   */
  getCycleIndex() {
    const { cycleIndex } = this;
    if (isNil(cycleIndex)) {
      throw new TypeError('Missing cycle index.');
    }
    return cycleIndex;
  }

  /**
   * Zjistí výšku, ve které se má médium zobrazit.
   * Hodnota odpovídá [CSS vlastnosti height](https://developer.mozilla.org/en-US/docs/Web/CSS/height).
   * @returns {string} Výška.
   */
  getHeight() {
    const { layer } = this;
    if (isNil(layer)) {
      return '100%';
    }
    return layer.height;
  }

  /**
   * Zjistí pořadí slajdu v rámci aktuálně přehrávaného cyklu.
   * Nastavuje se v intervalu `<0, length)`, kde `length` je počet slajdů v cyklu.
   * @returns {number} Pořadí.
   */
  getIterationIndex() {
    const { iterationIndex } = this;
    if (isNil(iterationIndex)) {
      throw new TypeError('Missing iteration index.');
    }
    return iterationIndex;
  }

  // TODO #458 Využít #getLayout v implementaci.
  // - Alternativa pro ScheduleInfo#getMedium()
  // - Na jeho přítomnost by se mělo reagovat tak, že pro každý Layer se vytvoří nová ScheduleStrategy.
  // - Touto změnou se stane Medium nepovinné.
  // - Nyní se již ScheduleInfo#layout používá pro rodičovskou šablonu. Refaktorovat před použitím!
  // /**
  //  * Zjistí šablonu k přehrání.
  //  * @returns {Layout} Šablona.
  //  */
  // getLayout () {
  //   const {layout} = this;
  //   if (isNil(layout)) {
  //     throw new TypeError(`Missing layout.`);
  //   }
  //   return layout;
  // }

  /**
   * Zjistí vzdálenost od levého okraje obrazovky, ve které se má médium zobrazit.
   * Hodnota odpovídá [CSS vlastnosti left](https://developer.mozilla.org/en-US/docs/Web/CSS/left).
   * @returns {string} Vzdálenost.
   */
  getLeft() {
    const { layer } = this;
    if (isNil(layer)) {
      return '0%';
    }
    return layer.left;
  }

  /**
   * Zjistí médium k přehrání.
   * @returns {Medium} Médium.
   */
  getMedium() {
    const { medium } = this;
    if (isNil(medium)) {
      throw new TypeError('Missing medium.');
    }
    return medium;
  }

  /**
   * Zjistí dobu přehrávání, po kterou má být slajd zobrazen.
   * V playlistu je to doba přehrávání tracku, jinak média.
   * @returns {number}
   */
  getPlayDuration() {
    // TODO Není propojeno s časovači, které se odpočítávají ve strategiích.
    // - Pouze se spoléhá na to, že implementace budou stejné.
    // - Promyslet a sjednotit určování doby přehrávání na jedno místo.
    const trackDuration = this.trackArray[0]?.playDuration;
    if (!isNil(trackDuration)) {
      return trackDuration;
    }
    const mediumDuration = this.medium?.playDuration;
    if (!isNil(mediumDuration)) {
      return mediumDuration;
    }
    throw new TypeError('Missing play duration.');
  }

  /**
   * Zjistí roztažení, které se má nastavit médiu.
   * @returns {StretchEnum} Roztažení.
   */
  getStretchEnum() {
    const mediumStretchEnum = this.medium?.stretchEnum;
    if (!isNil(mediumStretchEnum)) {
      return mediumStretchEnum;
    }
    const sessionStretchEnum = this.session?.stretchEnum;
    if (!isNil(sessionStretchEnum)) {
      return sessionStretchEnum;
    }
    throw new TypeError('Missing stretch.');
  }

  /**
   * Zjistí vzdálenost od horního okraje obrazovky, ve které se má médium zobrazit.
   * Hodnota odpovídá [CSS vlastnosti top](https://developer.mozilla.org/en-US/docs/Web/CSS/top).
   * @returns {string} Vzdálenost.
   */
  getTop() {
    const { layer } = this;
    if (isNil(layer)) {
      return '0%';
    }
    return layer.top;
  }

  /**
   * Zjistí URL, která se má nastavit jako zdroj.
   * @returns {Promise.<string>} URL.
   */
  getUrl() {
    return new Promise((resolve) =>
      resolve(
        this.getViewerType()
          .getUrl(this.medium)
          .then((url) => {
            if (isNil(url)) {
              throw new TypeError('Missing URL.');
            }
            return url;
          })
      )
    );
  }

  /**
   * Zjistí informace o typu média k přehrání.
   * @returns {ViewerType} Typ média.
   */
  getViewerType() {
    const viewerType = this.viewerRegistry.find(this.medium.type);
    if (isNil(viewerType)) {
      throw new TypeError('Missing viewer type.');
    }
    return viewerType;
  }

  /**
   * Zjistí hlasitost, která se má nastavit médiu.
   * @returns {number} Hlasitost [0-1].
   */
  getVolume() {
    const volume = [this.getMedium().volume]
      .concat(
        this.trackArray.map((track) => track.volume),
        this.playlistArray.map((playlist) => playlist.volume)
      )
      .reduce((accVolume, currentVolume) => {
        if (isNil(currentVolume)) {
          return accVolume;
        }
        if (isNil(accVolume)) {
          return currentVolume;
        }
        return accVolume * currentVolume;
      }, null);
    if (isNil(volume)) {
      throw new TypeError('Missing volume.');
    }
    return volume;
  }

  /**
   * Zjistí šířku, ve které se má médium zobrazit.
   * Hodnota odpovídá [CSS vlastnosti height](https://developer.mozilla.org/en-US/docs/Web/CSS/height).
   * @returns {string} Šířka.
   */
  getWidth() {
    const { layer } = this;
    if (isNil(layer)) {
      return '100%';
    }
    return layer.width;
  }

  /**
   * Zjistí umístění na ose Z, ve které se má médium zobrazit.
   * Hodnota odpovídá [CSS vlastnosti z-index](https://developer.mozilla.org/en-US/docs/Web/CSS/z-index).
   * @returns {string} Z-index.
   */
  getZIndex() {
    const { layer, layout } = this;
    // Slajdy mimo šablony mají z-index 0.
    if (isNil(layout)) {
      return '0';
    }
    // Pozadí šablony má z-index -1.
    if (isNil(layer)) {
      return '-1';
    }
    // Z-index vrstev šablony je povolený libovolný celočíselný.
    const zIndex = Number(layer.zIndex);
    if (!Number.isInteger(zIndex)) {
      throw new TypeError(`Invalid layer z-index ${zIndex}.`);
    }
    return String(zIndex);
  }

  /**
   * Zjistí, jestli informace obsahují cyklu do kterého slajd patří.
   * @returns {boolean} Příznak.
   */
  hasCycleIndex() {
    return !isNil(this.cycleIndex);
  }

  /**
   * Zjistí, jestli informace obsahují pořadí v rámci aktuálně přehrávaného cyklu.
   * @returns {boolean} Příznak.
   */
  hasIterationIndex() {
    return !isNil(this.iterationIndex);
  }

  // TODO #458 Využít #hasLayout v implementaci.
  // - Nyní se již ScheduleInfo#layout používá pro rodičovskou šablonu. Refaktorovat před použitím!
  // - Zvážit, jestli by nebylo lepší vracet v getLayout undefined (atomičnost).
  // /**
  //  * Zjistí, jestli informace obsahují šablonu k přehrání.
  //  * @returns {boolean} Příznak.
  //  */
  // hasLayout () {
  //   return !isNil(this.layout);
  // }

  /**
   * Zjistí, jestli informace obsahují médium k přehrání.
   * @returns {boolean} Příznak.
   */
  hasMedium() {
    return !isNil(this.medium);
  }

  /**
   * Zjistí, jestli informace obsahují nějaký objekt k přehrání.
   * Objekt k přehrání nemusí být dostupný u prázdného playlistu,
   * u prázdné šablony, bez obsahové úlohy apod.
   * @returns {boolean} Příznak.
   */
  isEmpty() {
    // TODO #458 Po přidání Layout jako možného koncového prvku informace, tak také tyto informace nebudou považovány za prázdné.
    // TODO #458 Jak rozlišovat mezi šablonou a mezi prázdným obsahem v šabloně?!
    // - parentLayouts + childLayout? - přeskládávat při merge?
    // - layout + medium + parentScheduleInfo - to by ale asi vyžadovalo podstatnější změny
    // - správným řešením asi bude odkaz na parentScheduleInfo a výpočet isEmpty jen pro aktuální úroveň.
    return isNil(this.medium);
    // TODO #458 Prototyp zpracování se zobrazením celé šablony.
    // - Nyní se již ScheduleInfo#layout používá pro rodičovskou šablonu. Refaktorovat před použitím!
    // return isNil(this.layout) && isNil(this.medium);
  }

  /**
   * Zjistí, jestli lze médium připravovat na pozadí,
   * zatímco je přehráváno jiné.
   * @returns {boolean}
   */
  isPreloadable() {
    // Pokud nejsou dostupné nějaké hodnoty, tak se preload povolí.
    // Jde o technický dluh, jinak by se preload vždy na podobném slajdu zaseknul, což není žádoucí.
    // Žádoucí je, aby byl takový slajd přeskočen při pokusu o jeho preload.
    const { viewerRegistry } = this;
    if (isNil(viewerRegistry)) {
      return true;
    }
    const { medium } = this;
    const mediumType = medium?.type;
    if (isNil(mediumType)) {
      return true;
    }
    const viewerType = viewerRegistry.find(mediumType);
    if (isNil(viewerType)) {
      return true;
    }
    // Výsledek se určí dle typu média.
    return viewerType.isPreloadable(medium);
  }

  // TODO Pokročilé porovnávání slajdů:
  // - Nyní je trochu zmatek, že `ScheduleInfo` se neporovnávají dle strict equality.
  // - Přitom třeba `ScheduleInfo#medium` už strict porovnáním je.
  // - Pokus o deep porovnání přes `lodash.isEqualWith` způsobuje problémy a odebírá možnost vydefinovat klíče k porovnání.
  // - Možná by mělo využívat obdobné metody `Medium#isEqual` a `ViewerType#isEqual`?
  // - Možná rovnou napojit na nějaký systém pro serializaci objektů podobně jako `#toJson` a podle nich porovnávat?
  // - Nebo netrvat na výčtu klíčů? Zamýšleno pro kompatibilitu s dalšími rozhraními v třídě a optimalizaci výkonu.
  /**
   * Zjistí, jestli je slajd podobný jinému.
   * Podobné slajdy mají stejné vazby na objekty, ale mohou být odlišnými instancemi.
   * Neprování se deep porovnání.
   * Porovnávají se pouze vlastnosti, které patří ke slajdům.
   * @param {ScheduleInfo} scheduleInfo Slajd k porovnání.
   * @returns {boolean} Příznak, jestli mají slajdy stejný obsah.
   */
  isEqual(scheduleInfo) {
    // TODO #458 Zrevidovat tuto funkcionalitu. Nemělo by se spíše cachovat podle URL, nebo něčeho podobného?
    // - Používá se asi jen pro recyklaci načítaného slajdu.
    // - Neměl by mít každý typ možnost určit jakou změnu akceptuje a jakou ne?
    //   To by byla asi pokročilá funkcionalita.
    // - Nyní bude při nejhorším stačit zde porovnávat vybrané hodnoty (URL, pozice, hlasitost, ...)
    //   To dává i větší smysl s ohledem třeba na zanoření do playlistu a stopy se 100% hlasitostí, která by nic neovlivnila.
    //   Aktuální algoritmus považuje tuto variantu za odlišné plány.
    // Validace druhého slajdu.
    if (!isObject(scheduleInfo)) {
      throw new TypeError(`Invalid schedule info ${scheduleInfo}.`);
    }
    if (this.isEmpty() && scheduleInfo.isEmpty()) {
      // Pokud nejsou načteny oba slajdy jde o schodu.
      return true;
    } else if (!this.isEmpty() && !scheduleInfo.isEmpty()) {
      // Pokud jsou načteny oba slajdy, tak se porovnají jejich významné hodnoty.
      return isEqual(this.toJSON(), scheduleInfo.toJSON());
    }
    // Jiná kombinace slajdů není shodou.
    return false;
  }

  /**
   * Zjistí, jestli lze médium zobrazit v aktuálních podmínkách.
   * @param {boolean} online Příznak, jestli je zařízení připojeno k síti.
   * @returns {boolean} Příznak, jestli lze médium zobrazit.
   */
  isViewable(online) {
    // Musí mít registr informací o typech médií.
    const { viewerRegistry } = this;
    if (isNil(viewerRegistry)) {
      return false;
    }
    // Musí mít médium a konkrétní typ.
    const mediumType = this.medium?.type;
    if (isNil(mediumType)) {
      return false;
    }
    // V registru není záznam odpovídající typu média.
    const viewerType = viewerRegistry.find(mediumType);
    if (isNil(viewerType)) {
      return false;
    }
    // Musí být online, nebo spustitelné v offline.
    if (!online && !viewerType.isOfflineable()) {
      return false;
    }
    const isIcalValid = this.trackArray.every((track) => {
      const { planIcal } = track;
      return (
        isNil(planIcal) ||
        new IcalPlanner().hasEventOnDate(planIcal, new Date())
      );
    });
    if (!isIcalValid) {
      return false;
    }
    // Musí mít volný zdroj k přehrání.
    const viewerSourceCreator = viewerType.getSourceCreator();
    return viewerSourceCreator.getLent() < viewerSourceCreator.getMax();
  }

  /** @inheritdoc */
  toJSON() {
    return {
      cycleIndex: this.cycleIndex,
      iterationIndex: this.iterationIndex,
      layerId: this.layer?.id ?? null,
      layoutId: this.layout?.id ?? null,
      mediumId: this.medium?.id ?? null,
      playlistIdArray: this.playlistArray.map((playlist) => playlist.id),
      sessionId: this.session?.id ?? null,
      trackIdArray: this.trackArray.map((track) => track.id)
    };
  }

  /** @inheritdoc */
  toString() {
    // TODO #458 Tohle může být problém jakmile třeba `ViewerType` bude mít cyklické odkazy.
    return `ScheduleInfo ${JSON.stringify(this)}`;
  }

  /**
   * Vytvoří nové informace sloučením s aktuálními.
   * @param {Object} [opts={}] Nepovinné parametry z konstruktoru.
   * @returns {ScheduleInfo} Vytvořené informace.
   */
  merge(opts) {
    return new ScheduleInfo(
      []
        .concat(
          // Položky, které se nahrazují.
          [
            'cycleIndex',
            'iterationIndex',
            'layer',
            'layout',
            'medium',
            'session',
            'viewerRegistry'
          ].map((key) => {
            let value;
            if (isUndefined(opts[key])) {
              value = this[key];
            } else {
              value = opts[key];
            }
            return [key, value];
          }),
          // Pole, které se slučují.
          ['playlistArray', 'trackArray'].map((key) => {
            let value;
            if (isUndefined(opts[key])) {
              value = this[key];
            } else {
              value = this[key].concat(opts[key]);
            }
            return [key, value];
          })
        )
        // Převod seznamu [key, value] na objekt.
        .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {})
    );
  }
}

export default ScheduleInfo;
